// =============================================================================
// The stock catalog — the list of things a shop can ask the kitchen for, and the
// photo of each one.
//
// Today every tablet fetches this from Apps Script (?action=stockCatalog, a
// public feed off the Google Sheet). This module reads the same list from
// Supabase instead, in EXACTLY the same shape, so nothing downstream changes.
//
// GATED OFF. CATALOG_READ_HOSTS is empty, so every tablet still reads the sheet.
// Flipping a site on is a reviewed one-line host add; reverting is deleting that
// line. On a flipped site the sheet is still there as the safety net — any
// failure at all falls straight back to it.
//
// Why an EMPTY result counts as a failure: the kitchen's read-flip guard treats
// "no catalog" as "don't filter", which would surface long-discontinued items on
// the storeroom screen. An empty catalog is a fault, not a valid state, so it
// falls back to the sheet rather than being trusted.
// =============================================================================
import { supabase } from "./supabaseClient.js";

// --- the gate ---------------------------------------------------------------
// Add a hostname here to flip that tablet; delete the line to revert it.
const CATALOG_READ_HOSTS = new Set([
  "mcr-kitchen.eatrolled.com",   // FLIPPED 18 Aug 2026 — catalog_list_readiness on PRODUCTION reads ready=true, 82 active items, 325 site links, 0 items with no available site. Revert = delete this line.
  "trafford.eatrolled.com",   // FLIPPED 17 Sep 2026 22:21 BST (#354) — Lane CG (SWITCHON_CATALOG_FOH.md); ON path reads site=tc, matching today's list exactly. Revert = delete this line.
]);

export function catalogReadEnabled() {
  if (typeof window === "undefined") return false;
  // `?catalogread=on|off` — a per-tablet override for a pre-shift look, the same
  // escape hatch sell-outs, stock and end-of-day already have. It survives
  // nothing (no storage, no cookie), so it is for checking a screen before the
  // shop opens, never for running the shop. Without it the only way to try this
  // is to deploy it to a real kitchen and hope.
  try {
    const q = new URLSearchParams(window.location.search).get("catalogread");
    if (q === "on") return true;
    if (q === "off") return false;
  } catch (e) {}
  return CATALOG_READ_HOSTS.has((window.location.hostname || "").toLowerCase());
}

// Public URL for a copied item photo. Best effort — a broken URL only costs a
// thumbnail, never the item itself.
function photoUrl(path) {
  if (!path) return "";
  if (/^https?:\/\//i.test(String(path))) return String(path);
  try { return supabase.storage.from("stock-catalog").getPublicUrl(path)?.data?.publicUrl || ""; }
  catch (e) { return ""; }
}

// The "responsible person" on a reorder card is a plain ROLE — "Adam" / "BOH" /
// "JJ" — never a PIN and never an email (0050 stores only responsible_role and
// refuses responsiblePin/responsibleEmail). This coerces whatever arrives into a
// clean, PIN-free string so a reorder card can NEVER show the gibberish
// "[object Object]" (a historical corruption where the field held a
// {tc,arndale,meadowhall,kitchen} object) and can never render a PIN that slipped
// into the field. Belt-and-braces on top of the feed, which already returns a
// clean string — this guarantees it at the reader boundary whatever the source.
export function cleanResponsibleRole(v, _depth = 0) {
  if (v == null) return "";
  if (typeof v === "object") {
    // never JSON-stringify an object into the UI; pull a sensible name out if the
    // old {role|name: "Adam"} / per-site object shape shows up, else blank.
    // Depth guard: real data is at most one object deep ({role:"Adam"}); bail on a
    // pathological self-referential object (unreachable via JSON, belt-and-braces).
    if (_depth > 3) return "";
    return cleanResponsibleRole(v.role ?? v.name ?? "", _depth + 1);
  }
  const s = String(v).trim();
  if (!s || /^\[object\b/i.test(s)) return "";   // an already-stringified object
  if (/^\d{3,}$/.test(s)) return "";             // a PIN that leaked into the field — never render it
  return s;
}

// The label a reorder card is allowed to SHOW for the responsible person. Unlike
// cleanResponsibleRole (which only strips objects and pure-digit PINs and would
// otherwise pass ANY string straight through), this is an ALLOWLIST: the only
// three roles the catalogue is meant to carry — 'Adam' / 'JJ' / 'BOH' — map to a
// fixed, human label, and everything else (blank, an unknown value, or an opaque
// 15-char token — 0510's cc_catalog_save stores whatever the Command Centre sends
// in responsible_role) becomes "". A card shows the label only when it is
// non-empty, so a token or a surprise value can never reach the screen. 'BOH'
// reads as "the kitchen (BOH)" because a shop asking "notify BOH" means the
// back-of-house kitchen.
export function responsibleLabel(role) {
  switch (cleanResponsibleRole(role).toLowerCase()) {
    case "adam": return "Adam";
    case "jj":   return "JJ";
    case "boh":  return "the kitchen (BOH)";
    default:     return "";
  }
}

// A row from stock_catalog_feed() -> the shape the screens already expect from
// the Apps Script feed. The Supabase photo wins; the Google Drive original is
// the fallback, so an item whose photo hasn't copied still shows a picture.
export function shapeCatalogItem(row) {
  const supa = photoUrl(row.imagePath);
  return {
    itemId: row.itemId,
    name: row.name,
    codeName: row.codeName || "",
    category: row.category || "",
    unit: row.unit || "",
    defaultQty: row.defaultQty ?? null,
    status: row.status || "active",
    imageUrl: supa || row.imageUrl || "",
    sites: Array.isArray(row.sites) ? row.sites : [],
    siteDefaults: row.siteDefaults && typeof row.siteDefaults === "object" ? row.siteDefaults : {},
    responsibleRole: cleanResponsibleRole(row.responsibleRole),
    updatedAt: row.updatedAt || null,
  };
}

// Read the catalog. Supabase first, sheet fallback on ANY problem.
//   sheetUrl: the existing feed URL (the fallback, and what unflipped tablets use)
//   site:     'tc' | 'arndale' | 'meadowhall' | 'kitchen' — or null for everything
// Returns { items, _source: "supabase" | "sheet" | "none", _reason? }.
//
// CONTRACT CHANGE (Lane CP, 16 Sep 2026): when BOTH sources fail this used to
// return bare `null`, and every caller silently kept whatever list it already
// had with no way to tell staff. shouldReplaceCatalog (below) already treats an
// empty `items` array exactly the same way a `null` was treated — "keep the
// cache" — so no caller needs to change for this. What changes is that a
// flipped screen can now show a third badge state ("neither source answered")
// instead of rendering nothing at all. _reason is plain English:
// "<supabase reason> / <sheet reason>".
//
// `enabled`, `rpc` and `httpGet` exist so the fallback rules can be unit-tested
// without a browser or a network — the app never passes them. The fallback is the
// safety-critical part of this file, so it gets a test rather than a promise.
// A kiosk must never hang waiting on Supabase — bound the wait and fall back to
// the sheet if it doesn't answer in time. Resolves to { __timeout: true } if the
// call hasn't settled within `ms`, so a SLOW (not just failed) read still lands
// the tablet back on the sheet rather than stalling the storeroom screen.
const CATALOG_TIMEOUT_MS = 2500;
function withTimeout(p, ms) {
  return new Promise((resolve) => {
    let settled = false;
    const t = setTimeout(() => { if (!settled) { settled = true; resolve({ __timeout: true }); } }, ms);
    Promise.resolve(p).then(
      (v) => { if (!settled) { settled = true; clearTimeout(t); resolve(v); } },
      (e) => { if (!settled) { settled = true; clearTimeout(t); resolve({ error: e }); } }
    );
  });
}

export async function fetchStockCatalog({
  sheetUrl,
  site = null,
  enabled = catalogReadEnabled(),
  rpc = (name, args) => supabase.rpc(name, args),
  httpGet = (u) => fetch(u),
  timeoutMs = CATALOG_TIMEOUT_MS,
} = {}) {
  // Tries the sheet and reports WHY on a failure (instead of just null), so a
  // double failure below can name what went wrong with both sources, not just
  // the first one.
  const sheetAttempt = async () => {
    if (!sheetUrl) return { ok: false, reason: "no sheet URL configured" };
    try {
      const r = await httpGet(sheetUrl);
      if (!r.ok) return { ok: false, reason: `sheet returned HTTP ${r.status || "error"}` };
      const d = await r.json();
      if (!d || !Array.isArray(d.items)) return { ok: false, reason: "sheet returned a malformed answer" };
      return { ok: true, items: d.items };
    } catch (e) { return { ok: false, reason: (e && e.message) || "sheet read threw" }; }
  };
  const fromSheet = async (why) => {
    if (why) { try { console.warn("[catalog] fell back to the sheet:", why); } catch (e) {} }
    const s = await sheetAttempt();
    return s.ok ? { items: s.items, _source: "sheet" } : null;
  };

  if (!enabled) return fromSheet(null);

  // Work out what Supabase gave us — a real answer, or a plain-English reason it
  // didn't — without throwing, so there is exactly one fallback path below and it
  // handles a double failure the same way as a single one.
  let supabaseAnswer = null;
  let supabaseReason = null;
  try {
    const res = await withTimeout(rpc("stock_catalog_feed", { p_site: site }), timeoutMs);
    if (res && res.__timeout) {
      supabaseReason = `supabase didn't answer within ${timeoutMs}ms`;
    } else {
      const { data, error } = res;
      if (error) supabaseReason = (error && error.message) || "supabase read failed";
      else if (!Array.isArray(data) || data.length === 0) supabaseReason = "supabase returned an empty catalog";
      else supabaseAnswer = { items: data.map(shapeCatalogItem), _source: "supabase" };
    }
  } catch (e) {
    supabaseReason = (e && e.message) || "supabase read threw";
  }
  if (supabaseAnswer) return supabaseAnswer;

  try { console.warn("[catalog] fell back to the sheet:", supabaseReason); } catch (e) {}
  const sheet = await sheetAttempt();
  if (sheet.ok) return { items: sheet.items, _source: "sheet" };

  // BOTH sources failed. This used to return null and every caller silently kept
  // whatever it had. shouldReplaceCatalog already treats an empty items array as
  // "keep the cache" (see below) — so this is safe for every existing caller —
  // the only change is a flipped screen can now show its third badge state
  // instead of nothing at all.
  const reason = `${supabaseReason} / ${sheet.reason}`;
  try { console.warn("[catalog] both sources failed:", reason); } catch (e) {}
  return { items: [], _source: "none", _reason: reason };
}

// =============================================================================
// The cache guard — an empty answer must never wipe a good list.
//
// fetchStockCatalog already treats an empty SUPABASE answer as a fault and falls
// back to the sheet (see the note at the top of this file). The SHEET's own
// answer had no such rule, on either path: every screen that reads the catalogue
// wrote whatever came back straight into state AND into localStorage, guarded
// only by `Array.isArray(d.items)` — and an EMPTY array passes that check.
//
// So one empty or unrecognised answer from the Apps Script feed — a site key it
// doesn't know, a redeploy mid-request, a truncated response — blanked the
// reorder screen AND overwrote the offline copy the tablet keeps for when the
// wi-fi drops. The tablet then had nothing to show even after the feed
// recovered, until the next successful fetch landed.
//
// This is that missing rule, one level up from fetchStockCatalog, applied to
// whichever source answered: a list we already have is only ever replaced by a
// real list. `src/reorders.jsx` has always done this (`&& d.items.length`); this
// makes the till screens agree with it.
//
//   current — the catalogue the screen is already showing (the cached list)
//   answer  — what fetchStockCatalog (or the bare sheet fetch) came back with
//
// Pure and exported so the rule gets a unit test rather than a promise.
// =============================================================================
export function shouldReplaceCatalog(current, answer) {
  // Malformed — no answer, or no items array. Ignore it and keep what's on
  // screen. This is exactly what every caller already did.
  if (!answer || !Array.isArray(answer.items)) return false;
  // A real list always wins.
  if (answer.items.length > 0) return true;
  // An empty list is accepted only when there is nothing to lose — a first load
  // on a tablet with no cached copy, where writing [] changes nothing anyone can
  // see. It is never allowed on top of a list we already have.
  return !(Array.isArray(current) && current.length > 0);
}

// =============================================================================
// The badge — one pure lookup so every screen shows the SAME words for the same
// source, instead of each screen keeping its own copy of the text.
//
//   "supabase" -> the live green badge (today's text and colour, unchanged)
//   "sheet"    -> the existing amber Fallback badge (today's text and colour,
//                 unchanged)
//   "none"     -> NEW (Lane CP, 16 Sep 2026). Neither source answered at all.
//                 Deliberately NOT the amber Fallback text: "Fallback" implies
//                 Google answered, which on this path it did not. Gets its own
//                 red, distinct from the amber "sheet" tone, so the one
//                 genuinely novel failure tonight's work is about reads as more
//                 urgent than the everyday "we used the safety net" amber.
//                 Deliberately does NOT say "showing the saved list": this text
//                 is shared by the cold-tablet-first-load case too (see
//                 shouldReplaceCatalog), where there IS no saved list — an
//                 empty screen plus a badge claiming to show a save would be
//                 its own small dishonesty (review of #378). The screen itself
//                 already shows whatever list is or isn't there; the badge's
//                 job is just to say why, not to describe what's on screen.
//   anything else (null, undefined, a value nobody has taught this function yet)
//   -> null, so a screen renders nothing rather than guessing at a colour.
//
// Pure and exported so the words are pinned by a test, not eyeballed on screen.
// =============================================================================
export function catalogBadge(source) {
  switch (source) {
    case "supabase":
      return { text: "Live — item list up to date", tone: "#6FAF8F" };
    case "sheet":
      return { text: "⚠ Fallback — item list from the backup copy", tone: "#FF7A5C" };
    case "none":
      return { text: "⚠ Offline copy — the server didn't answer", tone: "#D9363E" };
    default:
      return null;
  }
}

// =============================================================================
// Backup-pack items (JJ, 11 Oct 2026). Slow-to-reorder items keep one sealed
// backup pack in the store room; opening it is the running-low moment, and the
// Collect step words it that way. Ticked per item in the Command Centre catalogue
// and read here straight from Supabase on EVERY host — it does not ride the
// catalogue, so a tablet that still reads its catalogue from the sheet
// (Meadowhall) gets it too. Returns the item names, or null on any failure so the
// caller keeps its last good copy and a wifi blip never un-ticks an item.
// =============================================================================
export async function fetchBackupPackItems({
  rpc = (name, args) => supabase.rpc(name, args),
  timeoutMs = CATALOG_TIMEOUT_MS,
} = {}) {
  try {
    const res = await withTimeout(rpc("stock_backup_pack_feed", {}), timeoutMs);
    if (!res || res.__timeout || res.error || !Array.isArray(res.data)) return null;
    return res.data.filter((n) => typeof n === "string" && n.trim()).map((n) => n.trim());
  } catch (e) {
    return null;
  }
}

// Case-insensitive lookup set for the names above.
export function backupPackSet(names) {
  return new Set((Array.isArray(names) ? names : []).map((n) => String(n).trim().toLowerCase()));
}
